import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_API = `${BACKEND_URL}/api/admin`;
const http = axios.create({ withCredentials: true });

export default function AdminFeedback() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        const params = filter === "all" ? {} : { resolved: filter === "resolved" ? "true" : "false" };
        const r = await http.get(`${ADMIN_API}/feedback`, { params });
        setItems(r.data.items);
        setLoading(false);
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const toggle = async (item) => {
        try {
            const path = item.resolved ? "reopen" : "resolve";
            await http.post(`${ADMIN_API}/feedback/${item.id}/${path}`, {});
            toast.success(item.resolved ? "Reopened" : "Resolved");
            await load();
        } catch {
            toast.error("Action failed");
        }
    };

    return (
        <div className="p-10 max-w-5xl" data-testid="admin-feedback">
            <span className="text-[10px] tracking-[0.36em] uppercase text-gold-deep">Signal</span>
            <h1 className="mt-2 font-display text-5xl text-burgundy">
                <span className="italic">What</span> they're saying
            </h1>

            <div className="mt-8 inline-flex border border-burgundy/15 bg-cream" data-testid="fb-filter">
                {[
                    { id: "all", label: "All" },
                    { id: "open", label: "Open" },
                    { id: "resolved", label: "Resolved" },
                ].map(f => (
                    <button
                        key={f.id}
                        data-testid={`fb-filter-${f.id}`}
                        onClick={() => setFilter(f.id)}
                        className={`px-5 py-2 text-[10px] tracking-[0.28em] uppercase border-r border-burgundy/15 last:border-r-0 transition-colors ${
                            filter === f.id ? "bg-burgundy text-cream" : "text-ink hover:bg-burgundy/[0.05]"
                        }`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            <div className="mt-6 space-y-3">
                {loading && <div className="text-ink-muted text-sm text-center py-10">Loading…</div>}
                {!loading && items.length === 0 && (
                    <div className="text-ink-muted text-sm text-center py-10 border border-burgundy/15">No feedback yet.</div>
                )}
                {items.map(it => (
                    <div key={it.id} className="border border-burgundy/15 bg-cream p-4" data-testid={`feedback-row-${it.id}`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className={`text-[10px] tracking-[0.24em] uppercase px-2 py-0.5 ${
                                    it.category === "bug" ? "bg-burgundy text-cream" :
                                    it.category === "feature" ? "bg-gold/30 text-burgundy" : "bg-burgundy/10 text-burgundy"
                                }`}>
                                    {it.category}
                                </span>
                                {it.resolved && <span className="text-[10px] tracking-[0.24em] uppercase bg-gold/40 text-burgundy px-2 py-0.5">Resolved</span>}
                                <span className="text-[10px] tracking-[0.22em] uppercase text-ink-muted ml-2">
                                    {new Date(it.created_at).toLocaleString()}
                                </span>
                            </div>
                            <button
                                data-testid={`feedback-resolve-${it.id}`}
                                onClick={() => toggle(it)}
                                className="text-[10px] tracking-[0.24em] uppercase text-burgundy hover:underline underline-offset-4"
                            >
                                {it.resolved ? "Reopen" : "Mark resolved"}
                            </button>
                        </div>
                        <p className="mt-3 font-display text-base text-ink leading-relaxed whitespace-pre-wrap">{it.message}</p>
                        <div className="mt-2 text-[10px] tracking-[0.22em] uppercase text-ink-light">
                            session {it.session_id ? `${it.session_id.slice(0, 14)}…` : "—"} · path {it.path || "—"}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
